import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Link
} from '@mui/material';
import {
  Router as OnuIcon,
  Close as CloseIcon,
  Sync as SyncIcon,
  DeleteOutline as DesprovisionIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import api from '../../../services/useApi';
import '../../../styles/swal-overrides.css';

interface ONUCliente {
  cliente_id: number;
  contrato_id: number;
  nome: string;
  tituloContrato: string;
  contratoServicoId: number | null;
}

interface ONUEquipamento {
  id: number;
  marca: string;
  modelo: string;
  gpon_sn: string | null;
}

interface ONU {
  id: number;
  onu_id: number;
  serialNumber: string;
  description: string;
  descricao?: string;
  services_port?: number;
  txpower?: number;
  rxpower?: number;
  status?: string;
  distance?: number;
  ipv4_address?: string;
  cliente?: ONUCliente;
  equipamento?: ONUEquipamento;
}

interface OnusModalProps {
  open: boolean;
  onClose: () => void;
  oltId?: string;
  cardSlot?: number;
  portNumber?: string;
  portId?: number;
  cardId?: number;
}

const OnusModal: React.FC<OnusModalProps> = ({
  open,
  onClose,
  oltId,
  cardSlot,
  portNumber,
  portId,
  cardId
}) => {
  const navigate = useNavigate();
  const [onus, setOnus] = useState<ONU[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [desprovisioningId, setDesprovisioningId] = useState<number | null>(null);
  const [getInfoLoadingId, setGetInfoLoadingId] = useState<number | null>(null);

  // Função para buscar ONUs da porta
  const fetchOnus = async (portId: number) => {
    if (!oltId || !portId) return;
    setLoading(true);
    try {
      const response = await api.get(`/olts/onus/ports/${portId}`) as any;

      if (response.data?.success) {
        // Ordenar ONUs por ID (onu_id)
        const sortedOnus = (response.data.data || []).sort((a: ONU, b: ONU) => a.onu_id - b.onu_id);
        setOnus(sortedOnus);
      } else {
        toast.error('Erro ao carregar ONUs');
        setOnus([]);
      }
    } catch (err: any) {
      toast.error('Erro ao carregar ONUs');
      setOnus([]);
    } finally {
      setLoading(false);
    }
  };

  // Função para sincronizar ONUs da porta
  const syncOnus = async () => {
    if (!portId || !cardId) return;

    setSyncLoading(true);
    try {      
      const response = await api.post(`/olts/onus/slots/${cardId}/ports/${portId}/sync`) as any;

      if (response.data?.success) {
        const { onusCreated, onusUpdated } = response.data.data;
        toast.success(`Sincronização concluída: ${onusCreated} ONUs criadas, ${onusUpdated} ONUs atualizadas`);

        // Recarregar ONUs após sincronização (já ordenadas na função fetchOnus)
        await fetchOnus(portId);
      } else {
        toast.error('Erro ao sincronizar ONUs');
      }
      if (response.data?.debug_result) {
        await Swal.fire({
          title: 'Sincronização de ONUs',
          html: `<pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;max-height: 600px;">${response.data.debug_result}</pre>`,
          icon: 'info',
          width: '700px',
          customClass: {
            container: 'swal-zindex',
            htmlContainer: 'swal2-html-container-light'
          }
        });
      }
    } catch (err: any) {
      toast.error('Erro ao sincronizar ONUs');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleGetInfo = async (onu: ONU) => {
    setGetInfoLoadingId(onu.id);
    try {
      const response = await api.post('/olts/onus/get-info', { id: onu.id }) as any;
      const debugResult = response.data?.debug_result ?? response.data?.details ?? 'Sem dados retornados';
      const data = response.data?.data;
      let html = `<pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto; max-height: 500px; white-space: pre-wrap;">${String(debugResult).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
      if (data && Object.keys(data).length > 0) {
        const dataStr = JSON.stringify(data, null, 2);
        html = `<pre style="text-align: left; background: #1e3a5f; color: #e0e0e0; padding: 10px; border-radius: 5px; font-size: 11px; margin-bottom: 10px;">${dataStr.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>${html}`;
      }
      await Swal.fire({
        title: response.data?.success ? '📈 Informações da ONU' : 'Erro ao obter informações',
        html,
        width: '700px',
        customClass: { container: 'swal-zindex swal-onu-info', popup: 'swal-onu-info' }
      });
    } catch (err: any) {
      const errorData = err.response?.data;
      const debugResult = errorData?.debug_result ?? errorData?.error ?? err.message ?? 'Erro desconhecido';
      await Swal.fire({
        title: 'Erro ao obter informações',
        html: `<p style="text-align: left; margin-bottom: 10px;">${errorData?.message || 'Falha ao consultar a OLT.'}</p>
               <pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${String(debugResult).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`,
        icon: 'error',
        width: '600px',
        customClass: { container: 'swal-zindex swal-onu-info', popup: 'swal-onu-info' }
      });
    } finally {
      setGetInfoLoadingId(null);
    }
  };

  // Desprovisionar ONU da porta
  const handleDesprovisionar = async (onu: ONU) => {
    if (!window.confirm(`Desprovisionar ONU ${onu.onu_id} (${onu.serialNumber}) desta porta?`)) return;
    setDesprovisioningId(onu.id);
    try {
      const response = await api.post(`/olts/onus/desprovisionar`, { id: onu.id }) as any;
      if (response.data?.success) {
        toast.success('ONU desprovisionada com sucesso');
        await fetchOnus(portId!);
      } else {
        toast.error(response.data?.message || 'Erro ao desprovisionar ONU');
      }
    } catch (err: any) {
      const errorData = err.response?.data;

      if (errorData?.debug_result) {
        await Swal.fire({
          title: 'Erro de Desprovisionamento',
          html: `<p style="text-align: left; margin-bottom: 10px;">${errorData.message || 'Falha ao desprovisionar na OLT.'}</p>
                 <pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${errorData.debug_result}</pre>`,
          icon: 'error',
          width: '600px',
          customClass: {
            container: 'swal-zindex'
          }
        });
      } else {
        toast.error(errorData?.message || 'Erro ao desprovisionar ONU');
      }
    } finally {
      setDesprovisioningId(null);
    }
  };

  // Função para fechar modal
  const handleClose = () => {
    setOnus([]);
    onClose();
  };

  // Buscar ONUs quando o modal abrir
  useEffect(() => {
    if (open && oltId && portId) {
      fetchOnus(portId);
    }
  }, [open, oltId, portId]);

  const getSignalStrengthColor = (signal: number) => {
    if (signal > -22) return 'success';   // Bom
    if (signal >= -26) return 'warning';  // Razoável
    return 'error';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle sx={{ p: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <OnuIcon color="primary" />
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              ONUs da Porta {portNumber} - Slot {cardSlot} - Qtd: {onus.length}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={syncLoading ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={syncOnus}
              disabled={syncLoading || loading}
              size="small"
              sx={{ fontSize: '0.8rem' }}
            >
              {syncLoading ? 'Sincronizando...' : 'Sincronizar ONUs'}
            </Button>
            <IconButton onClick={handleClose} size="small" sx={{ p: 0.5 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5, overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{
            minHeight: 200,
            maxHeight: '60vh',
            overflow: 'auto',
            '& .MuiTableCell-root': {
              py: 0.5,
              px: 1.5,
              fontSize: '0.875rem',
              lineHeight: 1.2,
              whiteSpace: 'nowrap'
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.8rem'
            },
            '& .MuiTableHead-root': {
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
              boxShadow: '0 1px 0 0 rgba(224, 224, 224, 1)'
            }
          }}>
            <Table sx={{ tableLayout: 'auto', minWidth: 'max-content' }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Serial Number</strong></TableCell>
                  <TableCell><strong>Cliente</strong></TableCell>
                  <TableCell><strong>Equipamento</strong></TableCell>
                  <TableCell><strong>IP/Rede</strong></TableCell>
                  <TableCell><strong>Sinal (Rx/Tx)</strong></TableCell>
                  <TableCell><strong>Distância</strong></TableCell>
                  <TableCell><strong>Descrição</strong></TableCell>
                  <TableCell><strong>Service Port</strong></TableCell>
                  <TableCell align="center"><strong>Ação</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {onus.length > 0 ? (
                  onus.map((onu) => (
                    <TableRow key={onu.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {onu.onu_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={onu.status === 'online' ? 'Online' : onu.status === 'down' ? 'Down' : onu.status || 'Offline'}
                          size="small"
                          color={onu.status === 'online' ? 'success' : onu.status === 'down' ? 'error' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                          {onu.serialNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {onu.cliente ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                            <Link
                              component="button"
                              variant="body2"
                              sx={{ fontSize: '0.8rem' }}
                              onClick={() => {
                                navigate(`/clients/${onu.cliente!.cliente_id}`);
                                onClose();
                              }}
                            >
                              {onu.cliente.nome}
                            </Link>
                            <Typography component="span" variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                              —
                            </Typography>
                            <Link
                              component="button"
                              variant="body2"
                              sx={{ fontSize: '0.8rem' }}
                              onClick={() => {
                                navigate(`/contracts/${onu.cliente!.contrato_id}`);
                                onClose();
                              }}
                            >
                              {onu.cliente.tituloContrato}
                            </Link>
                            {onu.cliente.contratoServicoId != null && (
                              <Typography component="span" variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                {' '}(Serviço #{onu.cliente.contratoServicoId})
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {onu.equipamento ? (
                          <Link
                            component="button"
                            variant="body2"
                            sx={{ fontSize: '0.8rem', textAlign: 'left' }}
                            onClick={() => {
                              const search = (onu.equipamento?.gpon_sn || onu.serialNumber) || '';
                              navigate(`/equipments?search=${encodeURIComponent(search)}`);
                              onClose();
                            }}
                          >
                            {[onu.equipamento.marca, onu.equipamento.modelo].filter(Boolean).join(' / ') || '—'}
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {onu.ipv4_address || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.8rem',
                            color: onu.rxpower != null ? `${getSignalStrengthColor(onu.rxpower)}.main` : 'text.secondary'
                          }}
                        >
                          {onu.rxpower != null ? `${onu.rxpower} dBm` : '—'} / {onu.txpower != null ? `${onu.txpower} dBm` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {onu.distance != null ? `${onu.distance} m` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {(onu.descricao != null ? onu.descricao : onu.description) || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          [{onu.services_port != null ? String(onu.services_port).replace(/,/g, ', ') : '—'}]
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="info"
                          size="small"
                          aria-label="Obter informações"
                          onClick={() => handleGetInfo(onu)}
                          disabled={getInfoLoadingId !== null}
                          sx={{ fontSize: '0.75rem', mr: 0.5 }}
                        >
                          {getInfoLoadingId === onu.id ? <CircularProgress size={18} /> : <InfoIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          aria-label="Desprovisionar"
                          onClick={() => handleDesprovisionar(onu)}
                          disabled={desprovisioningId !== null}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {desprovisioningId === onu.id ? <CircularProgress size={18} /> : <DesprovisionIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <OnuIcon color="action" sx={{ fontSize: 48 }} />
                        <Typography variant="body1" color="text.secondary">
                          Nenhuma ONU encontrada
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Esta porta não possui ONUs conectadas
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={handleClose} color="primary" size="small" sx={{ fontSize: '0.8rem' }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnusModal; 